import {
  faCartShopping,
  faClock,
  faClockRotateLeft,
  faCreditCard,
  faHeart,
  faHourglass,
  faLifeRing,
  faMessage,
  faPeopleArrows,
  faRightFromBracket,
  faSliders,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, Transition } from "@headlessui/react";
import { useRouter } from "next/navigation";
import * as React from "react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { formatUserCode, secondsToHms } from "@audacia-hq/shared/utils";

import { useAuth } from "../../context/AuthContext";
import { useExpertList } from "../../context/ExpertListContext";
import { useMember } from "../../context/MemberContext";
import { useSurveys } from "../../context/SurveyContext";
import FidelityPointsBlock from "../FidelityPointsBlock";

interface Props {
  open: boolean;
  close: () => void;
}

const UserSlider: React.FC<Props> = ({ open, close }) => {
  const { t } = useTranslation("common");
  const { logout } = useAuth();
  const { member } = useMember();
  const { renderSurveyBanner } = useSurveys();
  const { clearFilters } = useExpertList();
  const router = useRouter();

  const remaining = secondsToHms(member?.profile?.remainingTime || 0);

  const links = [
    {
      label: t("menu.purchase"), link: "/packs", icon: faCartShopping, className: "bg-slate-100", dataPW: "purchaseMenu",
    },
    {
      label: t("menu.favorites"),
      link: "/?favorite=true",
      icon: faHeart,
      clickAction: () => clearFilters({ frontFilters: { expertType: ["FAVORITE"] } }),
      dataPW: "favoritesMenu",
    },
    {
      label: t("menu.myPurchases"), link: "/my-purchases", icon: faCreditCard, dataPW: "myPurchasesMenu",
    },
    {
      label: t("menu.myConsultations"), link: "/my-consultations", icon: faPeopleArrows, dataPW: "myConsultationsMenu",
    },
    {
      label: t("menu.myMinuteHistory"), link: "/my-minutes-history", icon: faClockRotateLeft, dataPW: "myMinuteHistoryMenu",
    },
    {
      label: t("menu.myQueues"), link: "/my-queues", icon: faHourglass, dataPW: "myQueuesMenu",
    },
    {
      label: t("menu.messages"), link: "/messages", icon: faMessage, dataPW: "messagesMenu",
    },
    {
      label: t("menu.supportTickets"), link: "/my-tickets", icon: faTicket, dataPW: "supportTicketsMenu",
    },
    {
      label: t("menu.alerts"), link: "/my-alerts", icon: faClock, dataPW: "alertsMenu",
    },
    {
      label: t("menu.addictionMeasures"), link: "/addiction-measures", icon: faLifeRing, dataPW: "addictionMeasuresMenu",
    },
    {
      label: t("menu.memberSettings"), link: "/member-settings/profile", icon: faSliders, dataPW: "memberSettingsMenu",
    },
  ];

  const onNavigate = (link: string) => {
    router.push(link);
    close();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-[50]"
        onClose={close}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto relative w-screen max-w-md">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-300 hover:text-white focus:outline-hidden focus:ring-2 focus:ring-white"
                      onClick={() => close()}
                    >
                      <span className="sr-only">Close panel</span>
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl border-l-8 border-slate-300">
                  <div className="my-6 px-4 sm:px-6">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      <>
                        {" "}
                        {t("welcome")}
                        {" "}
                        {member?.profile.pseudo}
                      </>
                    </Dialog.Title>
                  </div>

                  <div className="px-4 sm:px-6">
                    <div className="my-3">
                      <div className="text-sm">{t("memberCode")}</div>
                      <div className="text-slate-900 font-semibold">
                        {
                          member?.profile.memberCode
                            ? formatUserCode(member.profile.memberCode, { padLength: 8, padChar: "0", format: "4 4" })
                            : ""
                        }

                      </div>
                    </div>
                    <div className="my-3">
                      <div className="text-sm">{t("memberRemainingTime")}</div>
                      <div className={`${remaining.isNegative() ? "text-red-500" : "text-slate-900"} font-semibold`}>
                        {remaining.toTimeString(t)}
                      </div>
                    </div>
                  </div>
                  <div className="relative my-6 flex-1">
                    {links.map(({
                      label, link, icon, className, clickAction, dataPW,
                    }) => (
                      <div className={`border-b border-slate-300 text-lg pl-5 py-2 ${className}`} key={link}>
                        <button
                          type="button"
                          data-pw={dataPW}
                          onClick={() => {
                            if (clickAction) clickAction();
                            onNavigate(link);
                          }}
                        >
                          <div className="hover cursor-pointer">
                            {link !== "/my-alerts" ? (
                              <FontAwesomeIcon
                                icon={icon}
                                className="pr-2 mr-2 w-8 border-r border-slate-500"
                              />
                            ) : (
                              <div className="inline-block pr-2 mr-2 border-r border-slate-500 w-10 h-[15.75px] relative top-[2px]">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 493 511.92" className="h-full mx-auto">
                                  <path d="M277.16 41.75c49.87 6.77 94.55 29.88 128.47 63.79 40.67 40.67 65.83 96.87 65.83 158.93 0 62.08-25.15 118.28-65.83 158.96a227.22 227.22 0 0 1-25.34 21.83l27.24 38.33c5.68 8.18 3.65 19.42-4.54 25.11-8.19 5.68-19.44 3.65-25.12-4.54l-28.28-39.78c-30.84 15.91-65.83 24.89-102.92 24.89-37.7 0-73.23-9.28-104.43-25.69l-26.59 39.71c-5.54 8.28-16.76 10.5-25.04 4.95-8.29-5.54-10.5-16.75-4.95-25.03l26.07-38.95a225.636 225.636 0 0 1-24-20.83c-40.68-40.68-65.84-96.89-65.84-158.96 0-62.07 25.16-118.26 65.84-158.94 36.44-36.43 85.34-60.39 139.74-65.03 16.45-1.4 33.38-.96 49.69 1.25zm204.53 102.98c17.3-41.28 15.24-84.52-9.51-113.49-29.7-34.77-83.39-38.75-133.26-14.3 53.01 36.36 101.12 78.78 142.77 127.79zm-470.15 1.35C-6.1 104.02-4.01 59.97 21.21 30.45 51.47-4.97 106.18-9.03 156.99 15.88c-54 37.06-103.03 80.26-145.45 130.2zm269.3 101.47 67.65-1.18c9.97-.17 18.19 7.76 18.36 17.73.18 9.97-7.76 18.19-17.73 18.37l-69.51 1.21c-6.61 11.32-18.89 18.93-32.94 18.93-21.05 0-38.12-17.08-38.12-38.13 0-14.52 8.13-27.15 20.08-33.58v-87.35c0-9.97 8.07-18.05 18.04-18.05 9.97 0 18.06 8.08 18.06 18.05v87.35a38.324 38.324 0 0 1 16.11 16.65zm99.27-116.5c-34.14-34.14-81.32-55.26-133.43-55.26-52.1 0-99.28 21.12-133.42 55.26-34.15 34.14-55.27 81.32-55.27 133.43 0 52.11 21.12 99.28 55.27 133.43 34.14 34.14 81.31 55.26 133.41 55.26 52.12 0 99.29-21.12 133.43-55.26 34.14-34.15 55.28-81.32 55.28-133.44 0-52.1-21.13-99.27-55.27-133.42z" />
                                </svg>
                              </div>
                            )}
                            {label}
                          </div>
                        </button>
                      </div>
                    ))}

                    <div className="hover text-lg pl-5 py-2">
                      <button
                        type="button"
                        onClick={() => {
                          logout().then(() => close());
                        }}
                        data-pw="logoutMenu"
                      >
                        <FontAwesomeIcon
                          icon={faRightFromBracket}
                          className="pr-2 mr-2 w-8 border-r border-slate-500"
                        />
                        {t("menu.logout")}
                      </button>
                    </div>
                  </div>

                  {renderSurveyBanner(false, true, close)}

                  <div className="p-2">
                    <FidelityPointsBlock fidelityPoints={member?.profile.fidelityPoints ?? 0} />
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default UserSlider;
